import { customElement } from "lit/decorators.js";
import { html, TemplateResult, PropertyValues } from "lit";
import { annotationState } from "@/state/Annotations";
import { initTooltips } from "@/utilities";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { Tooltip } from "bootstrap";

/**
 * This component represents the toggles to show/hide annotations.
 * It contains the buttons to show all annotations, only important annotations or no annotations.
 *
 * @element d-annotations-toggles
 */
@customElement("d-annotations-toggles")
export class AnnotationsToggles extends DodonaElement {
    protected updated(): void {
        initTooltips(this);
    }

    private setAll(): void {
        annotationState.visibility = "all";
        this.hideTooltips();
    }

    private setImportant(): void {
        annotationState.visibility = "important";
        this.hideTooltips();
    }

    private setNone(): void {
        annotationState.visibility = "none";
        this.hideTooltips();
    }

    private hideTooltips(): void {
        const buttons = this.renderRoot.querySelectorAll<HTMLButtonElement>("button[data-bs-toggle='tooltip']");
        buttons.forEach((btn) => {
            Tooltip.getInstance(btn)?.hide();
        });
    }

    private isActive(value: "all" | "important" | "none"): boolean {
        return annotationState.visibility === value;
    }

    private tabIndexFor(value: "all" | "important" | "none"): number {
        // "roving tabindex": only the active one is tabbable
        return this.isActive(value) ? 0 : -1;
    }

    private onKeyDown(e: KeyboardEvent): void {
        const order: Array<"all" | "important" | "none"> = ["all", "important", "none"];
        const current = annotationState.visibility;
        const idx = order.indexOf(current);

        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;

        e.preventDefault();
        const nextIdx = e.key === "ArrowLeft" ? (idx - 1 + order.length) % order.length : (idx + 1) % order.length;
        annotationState.visibility = order[nextIdx];

        // Move focus to the newly active "radio"
        queueMicrotask(() => {
            const btn = this.renderRoot.querySelector<HTMLButtonElement>(`button[data-value="${order[nextIdx]}"]`);
            btn?.focus();
        });
    }

    protected render(): TemplateResult {
        const title = I18n.t("js.annotations.toggles.title");
        return html`
            <span class="diff-switch-buttons switch-buttons">
                <span>${title}</span>
                <div class="btn-group btn-toggle" role="radiogroup" aria-label="${title}" @keydown=${this.onKeyDown}>
                    <button
                        type="button"
                        data-value="all"
                        class="btn annotation-toggle ${this.isActive("all") ? "active" : ""}"
                        role="radio"
                        aria-checked="${this.isActive("all") ? "true" : "false"}"
                        tabindex="${this.tabIndexFor("all")}"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-trigger="hover focus"
                        title="${I18n.t("js.annotations.toggles.show_all")}"
                        aria-label="${I18n.t("js.annotations.toggles.show_all")}"
                        @click=${this.setAll}
                    >
                        <i class="mdi mdi-comment-multiple-outline" aria-hidden="true"></i>
                        <span class="visually-hidden">${I18n.t("js.annotations.toggles.show_all")}</span>
                    </button>

                    <button
                        type="button"
                        data-value="important"
                        class="btn annotation-toggle ${this.isActive("important") ? "active" : ""}"
                        role="radio"
                        aria-checked="${this.isActive("important") ? "true" : "false"}"
                        tabindex="${this.tabIndexFor("important")}"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-trigger="hover focus"
                        title="${I18n.t("js.annotations.toggles.show_errors")}"
                        aria-label="${I18n.t("js.annotations.toggles.show_errors")}"
                        @click=${this.setImportant}
                    >
                        <i class="mdi mdi-comment-alert-outline" aria-hidden="true"></i>
                        <span class="visually-hidden">${I18n.t("js.annotations.toggles.show_errors")}</span>
                    </button>

                    <button
                        type="button"
                        data-value="none"
                        class="btn annotation-toggle ${this.isActive("none") ? "active" : ""}"
                        role="radio"
                        aria-checked="${this.isActive("none") ? "true" : "false"}"
                        tabindex="${this.tabIndexFor("none")}"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-trigger="hover focus"
                        title="${I18n.t("js.annotations.toggles.hide_all")}"
                        aria-label="${I18n.t("js.annotations.toggles.hide_all")}"
                        @click=${this.setNone}
                    >
                        <i class="mdi mdi-comment-remove-outline" aria-hidden="true"></i>
                        <span class="visually-hidden">${I18n.t("js.annotations.toggles.hide_all")}</span>
                    </button>
                </div>
            </span>
        `;
    }
}
